import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { Loader2 } from "lucide-react";
import * as React from "react";

import { cn } from "./lib/utils.js";

const buttonVariants = cva(
	"inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
	{
		variants: {
			variant: {
				default:
					"bg-primary text-primary-foreground shadow hover:bg-primary/90",
				primary:
					"bg-primary text-primary-foreground shadow hover:bg-primary/90",
				destructive:
					"bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90",
				outline:
					"border border-input bg-background shadow-sm hover:bg-accent hover:text-accent-foreground",
				secondary:
					"bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80",
				ghost: "hover:bg-accent hover:text-accent-foreground",
				link: "text-primary underline-offset-4 hover:underline",
			},
			size: {
				default: "h-11 px-4 py-2",
				md: "h-11 px-4 py-2",
				sm: "h-11 rounded-md px-3 text-xs",
				lg: "h-12 rounded-md px-8",
				icon: "h-11 w-11",
			},
		},
		defaultVariants: {
			variant: "default",
			size: "default",
		},
	},
);

export interface ButtonProps
	extends React.ButtonHTMLAttributes<HTMLButtonElement>,
		VariantProps<typeof buttonVariants> {
	asChild?: boolean;
	isLoading?: boolean;
	fullWidth?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
	(
		{
			className,
			variant,
			size,
			asChild = false,
			isLoading = false,
			fullWidth = false,
			disabled,
			children,
			...props
		},
		ref,
	) => {
		const Comp = asChild ? Slot : "button";
		const isDisabled = disabled || isLoading;
		const sanitizedChildren = asChild
			? (React.Children.toArray(children).find((child) =>
					React.isValidElement(child),
				) ?? null)
			: children;

		if (asChild) {
			// Radix Slot needs exactly one valid React element. Reduce to the
			// first valid child so callers can safely interleave whitespace
			// or conditional fragments without crashing Slot.
			return (
				<Comp
					className={cn(
						buttonVariants({ variant, size }),
						fullWidth && "w-full",
						className,
					)}
					ref={ref}
					{...props}
				>
					{sanitizedChildren}
				</Comp>
			);
		}

		return (
			<Comp
				className={cn(
					buttonVariants({ variant, size }),
					fullWidth && "w-full",
					className,
				)}
				ref={ref}
				disabled={isDisabled}
				{...props}
			>
				{isLoading && !asChild ? (
					<Loader2 className="h-4 w-4 animate-spin" />
				) : null}
				{sanitizedChildren}
			</Comp>
		);
	},
);
Button.displayName = "Button";

export { Button, buttonVariants };
